<script setup lang="ts">
import { type PollTailwindClasses } from '@@/bits/post_color'
import type { Id } from '@@/types'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { computed, toRefs } from 'vue'

const emit = defineEmits<{
  (e: 'select', choiceId: Id): void
}>()

const props = withDefaults(
  defineProps<{
    selected: boolean
    choiceText: string
    choiceId: Id
    index: number
    numChoices: number
    darkMode?: boolean | 'auto'
    displayMode?: 'expanded' | 'default' | 'slideshow'
    isStream?: boolean
    pollColors: PollTailwindClasses
    interactionsDisabled?: SurfacePostPollChoiceInteractionsDisabled
    pollScaleMultiplier?: number
  }>(),
  {
    darkMode: 'auto',
    displayMode: 'default',
    isStream: false,
    interactionsDisabled: SurfacePostPollChoiceInteractionsDisabled.None,
    pollScaleMultiplier: 1,
  },
)

const {
  selected,
  choiceText,
  choiceId,
  darkMode,
  displayMode,
  isStream,
  numChoices,
  pollColors,
  index,
  interactionsDisabled,
  pollScaleMultiplier,
} = toRefs(props)

const isDisplayModeExpanded = computed(() => displayMode.value === 'expanded')
const isDisplayModeSlideshow = computed(() => displayMode.value === 'slideshow')
const isDisplayModeExpandedOrSlideshow = computed(() => isDisplayModeExpanded.value || isDisplayModeSlideshow.value)

const onSelect = () => {
  if (
    interactionsDisabled.value === SurfacePostPollChoiceInteractionsDisabled.All ||
    interactionsDisabled.value === SurfacePostPollChoiceInteractionsDisabled.Select
  )
    return
  emit('select', choiceId.value)
}
</script>

<script lang="ts">
export enum SurfacePostPollChoiceInteractionsDisabled {
  All,
  Select,
  None,
}
</script>

<template>
  <div
    :data-testid="`surfacePostPollChoiceButton${index + 1}`"
    :class="[
      'flex flex-col cursor-pointer mx-1 no-drag no-highlight-color',
      interactionsDisabled === SurfacePostPollChoiceInteractionsDisabled.None
        ? 'cursor-pointer'
        : 'pointer-events-none',
    ]"
    @click="onSelect"
    @keydown.enter="onSelect"
  >
    <div
      :tabindex="interactionsDisabled === SurfacePostPollChoiceInteractionsDisabled.None ? 0 : -1"
      :class="[
        'flex items-center py-2',
        isDisplayModeSlideshow ? 'rounded-2xl' : 'rounded-xl',
        {
          'ps-7.5': isDisplayModeSlideshow && pollScaleMultiplier === 2.5,
          'ps-6': isDisplayModeSlideshow && pollScaleMultiplier === 2,
          'ps-3': (!isDisplayModeSlideshow && isStream) || (isDisplayModeSlideshow && pollScaleMultiplier === 1),
          'ps-2': !isDisplayModeSlideshow && !isStream,
        },
        interactionsDisabled === SurfacePostPollChoiceInteractionsDisabled.None && {
          [pollColors.pollChoiceHoverBackground.light]: darkMode === false,
          [pollColors.pollChoiceHoverBackground.dark]: darkMode === true,
          [pollColors.pollChoiceHoverBackground.auto]: darkMode === 'auto',
        },
        interactionsDisabled === SurfacePostPollChoiceInteractionsDisabled.None && 'focus-visible:ring-2 active:ring-2',
        interactionsDisabled === SurfacePostPollChoiceInteractionsDisabled.None && {
          'active:ring-grape-500 focus-visible:ring-grape-500': darkMode === false,
          'active:ring-canary-500 focus-visible:ring-canary-500': darkMode === true,
          'active:ring-grape-500 dark:active:ring-canary-500 focus-visible:ring-grape-500 dark:focus-visible:ring-canary-500':
            darkMode === 'auto',
        },
        index === numChoices - 1 && 'mb-1',
      ]"
      :aria-checked="selected"
    >
      <OzIcon
        :class="[
          'pointer-events-none',
          selected
            ? {
                'text-grape-500': darkMode === false,
                'text-canary-500': darkMode === true,
                'text-grape-500 dark:text-canary-500': darkMode === 'auto',
              }
            : {
                'text-dark-text-200': darkMode === false,
                'text-light-text-200': darkMode === true,
                'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
              },
        ]"
        :name="selected ? 'radio_selected' : 'radio_unselected'"
        :size="isDisplayModeSlideshow ? Math.max(20, 10 * pollScaleMultiplier) : 16"
      />
      <p
        :data-testid="`surfacePostPollChoiceText${index + 1}`"
        :class="[
          'ps-3 break-word-anywhere',
          {
            'pe-7.5': isDisplayModeSlideshow && pollScaleMultiplier === 2.5,
            'pe-6': isDisplayModeSlideshow && pollScaleMultiplier === 2,
            'pe-3': !isDisplayModeSlideshow || (isDisplayModeSlideshow && pollScaleMultiplier === 1),
          },
          isDisplayModeExpandedOrSlideshow || isStream ? 'text-body' : 'text-body-small',
          {
            'text-dark-text-100': darkMode === false,
            'text-light-text-100': darkMode === true,
            'text-dark-text-100 dark:text-light-text-100': darkMode === 'auto',
          },
          selected && 'font-semibold',
        ]"
      >
        {{ choiceText }}
      </p>
    </div>

    <OzDivider
      v-if="index !== numChoices - 1"
      :class="[
        'my-1',
        {
          'ms-16': isDisplayModeSlideshow && pollScaleMultiplier === 2.5,
          'ms-13': isDisplayModeSlideshow && pollScaleMultiplier === 2,
          'ms-10': isDisplayModeSlideshow && pollScaleMultiplier === 1,
          'ms-9': !isDisplayModeSlideshow || (isDisplayModeSlideshow && pollScaleMultiplier === 1),
        },
        {
          'me-8': isDisplayModeSlideshow && (pollScaleMultiplier === 2.5 || pollScaleMultiplier === 2),
          'me-4': isDisplayModeExpandedOrSlideshow,
          'me-3': !isDisplayModeExpandedOrSlideshow && isStream,
          'me-1': !isDisplayModeExpandedOrSlideshow && !isStream,
        },
      ]"
      :color-scheme="OzDividerColorScheme.Solid"
      :dark-mode="darkMode"
    />
  </div>
</template>
<style scoped>
.no-highlight-color {
  -webkit-tap-highlight-color: transparent; /** On android chrome there is a blue background while selecting a poll choice we add this to remove the blue background
  The poll choice has styles to indicate it is selected so this is not needed
  */
}
</style>
